import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import BoardGrid from "@/components/game/BoardGrid";
import CoastalRow from "@/components/game/CoastalRow";
import MarketPanel from "@/components/game/MarketPanel";
import ClimateBankPanel from "@/components/game/ClimateBankPanel";
import LargeCardSlot from "@/components/game/LargeCardSlot";
import TileCardTabs from "@/components/game/TileCardTabs";
import CardActions from "@/components/game/CardActions";
import WeatherOverlay from "@/components/game/WeatherOverlay";
import { tileCardsFor } from "@/lib/tileCards";
import PlayersColumn from "@/components/game/PlayersColumn";
import HandRow from "@/components/game/HandRow";
import BoardArea from "@/components/game/BoardArea";
import CardZoom from "@/components/game/CardZoom";
import TopBar from "@/components/game/TopBar";
import EventPanel, { WeatherChip, eventMode, deckLeft, globalLeft, lastWeather, removalLines } from "@/components/game/WeatherPanel";
import { EventDeck } from "@/components/game/Piles";
import { globalEffectText } from "@/components/game/WeatherCardReveal";
import { weatherCardSrc, globalCardSrc, preloadImage, preloadWeatherArt } from "@/lib/artAssets";
import ResponseFlow from "@/components/game/ResponseFlow";
import DemolishConfirm from "@/components/game/DemolishConfirm";
import ActionCardPanel, { ActionCardWaiting, ActionResultFlash } from "@/components/game/ActionCardPanel";
import ConnectivityOverlay from "@/components/game/ConnectivityOverlay";
import LiveScoreDialog from "@/components/game/LiveScoreDialog";
import PlacementPreview, { ScoreFly } from "@/components/game/PlacementPreview";
import EndgameOverlay from "@/components/game/EndgameOverlay";
import FinalColumn from "@/components/game/FinalColumn";
import { downloadBoardImage, downloadGameLog } from "@/lib/endgameExport";
import SettingsPanel from "@/components/game/SettingsPanel";
import useDesignerMode from "@/hooks/useDesignerMode";
import useSlotFit from "@/hooks/useSlotFit";
import SetupChoice from "@/components/game/SetupChoice";
import { useSeaRise } from "@/hooks/useSeaRise";
import { S, useMuted, GREEN_SOUND_KEYS, BLUE_SOUND_KEYS } from "@/lib/sound";
import { flyCoins } from "@/lib/coinFlights";
import Coin from "@/components/game/Coin";
import Stage, { toStage } from "@/components/game/Stage";
import Rulebook from "@/components/game/Rulebook";
import { computeGuidance, eventActor } from "@/lib/guidance";
import { useFocusMode, useSeat } from "@/hooks/useDevicePrefs";
import { useOnlineGame } from "@/hooks/useOnlineGame";
import { useWeatherTimeline } from "@/hooks/useWeatherTimeline";
import { squareShort } from "@/lib/squares";
import { heightOf } from "@/lib/terrain";
import { wallEdgeOptions } from "@/components/game/EdgeWalls";
import { WALL_KEYS } from "@/components/game/CellTokens";
import {
  clearOnline,
  skipGoneSeat,
  discardCard,
  discardActionCard,
  drawCard,
  drawDeck,
  payBlind,
  skipPick,
  getGame,
  placeStartingCube,
  randomizeSetup,
  playAction,
  playBuild,
  endTurn,
  takeActionCard,
  wipeMarket,
  getWeatherFaces,
  weatherProtectionDecide,
  responsesDecide,
  bankPay,
  bankPropose,
  demolishPreview,
  demolish,
  actionCardSelectCell,
  actionCardBack,
  actionCardConfirm,
  actionCardCancel,
  getConnectivity,
  PLAYER_COLORS,
  getSettings,
  getScoring,
  getBuildPreview,
} from "@/lib/api";

// LAYOUT F (stage 1440 x 900 wide, 1280-1440 x 900 compact): top bar 58 | left 285 (players +
// SELECTED CARD) · centre board · right 470 (MARKETPLACE with the weather / global decks and the Climate Bank tab, then YOUR HAND). Compact: side columns narrower.
const LAYOUT = {
  wide: { left: 285, right: 470, slotW: 240, cardW: 104 },
  compact: { left: 262, right: 424, slotW: 214, cardW: 93 },
};

// PAY UPFRONT: why a take (market card, blind draw, action card) is greyed.
const TAKE_DEBT_REASON = "You are in debt: you can't take cards";
const TAKE_MONEY_REASON = "Not enough money to take this card";

const RESPONSE_NOTES = {
  protection_card: "Response card: played when a weather event (rain, heat or storm surge) hits your squares",
};

// Card-detail slot sized live so the card + its buttons end level with the bottom of the board
// (between the layout's slotW and the left column width).
function FitSlot({ layout, children }) {
  const [fit, regionRef] = useSlotFit({ min: layout.slotW, max: layout.left });
  return children(fit, regionRef);
}

// TERRAIN 1 of 3: a game saved before terrain heights can't be continued (server 410 old_version).
function OldVersionNotice({ detail, onNewGame }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="old-version-notice" style={{ background: "var(--paper, #F6F4EF)" }}>
      <div className="flex flex-col items-center text-center gap-5 rounded-2xl px-8 py-8" style={{ maxWidth: 460, background: "var(--card)", border: "1px solid var(--card-border)", boxShadow: "var(--shadow-card)" }}>
        <div className="label-kicker text-[color:var(--ink-3)]">Porosity</div>
        <p data-testid="old-version-text" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35, color: "var(--ink)" }}>
          {detail?.message || "This game was made with an older version of Porosity and can't be continued"}
        </p>
        <button type="button" data-testid="old-version-new-game" onClick={onNewGame} className="rounded-full px-6 py-2.5" style={{ background: "var(--ink)", color: "var(--card)", fontWeight: 700, fontSize: 15 }}>
          Start a new game
        </button>
      </div>
    </div>
  );
}

export default function GameScreen() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  // Online (saved ticket for this game): polling, version guard, status-code handling.
  const { online, viewer, reconnecting, accept, fail, reload, isTableError, oldVersion, setOldVersion } = useOnlineGame(gameId, setGame);
  const [faces, setFaces] = useState(null);
  const [selectedCardIdx, setSelectedCardIdx] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  // Held action card selected for "Play action card" (Player.action_cards id).
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [demolishState, setDemolishState] = useState(null);
  const [connectivity, setConnectivity] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const designer = useDesignerMode();
  const [showLiveScore, setShowLiveScore] = useState(false);
  // Rulebook overlay: purely visual, never touches game or selection state.
  const [showRulebook, setShowRulebook] = useState(false);
  const closeRulebook = useCallback(() => setShowRulebook(false), []);
  // Focus dimming (device setting) + "This screen plays as" seat (per game, per device).
  const [focusOn, toggleFocus] = useFocusMode();
  const [seatPref, setSeat] = useSeat(gameId);
  // "Turn done." shows for 2 s after a player's turn ends.
  const [turnDone, setTurnDone] = useState(null);
  const prevTurnRef = useRef(null);
  // Seat-mode polling: drop results that raced with a local action.
  const actionSeqRef = useRef(0);
  const busyRef = useRef(false);
  const [rules, setRules] = useState(null);
  const cardCost = (c) => rules?.economy?.card_cost_overrides?.[c.key] ?? c.cost;
  const [scores, setScores] = useState(null);
  const [resiliences, setResiliences] = useState(null);      // public: resilience of every player, live
  const [buildPreview, setBuildPreview] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);
  const [scoreFly, setScoreFly] = useState(null);
  const [settingsRefreshKey, setSettingsRefreshKey] = useState(0);
  const [zoom, setZoom] = useState(null);
  // Card shown in the large slot: the last hand card or market row clicked.
  const [viewed, setViewed] = useState(null);
  // Board square being read in the large slot: { cell: "r,c", tab } (tab = card index on that square).
  const [tileView, setTileView] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const closeZoom = useCallback(() => setZoom(null), []);
  const [logOpen, setLogOpen] = useState(false);
  // End screen: the final score overlay can be closed to look at the final board (read-only).
  const [finalOpen, setFinalOpen] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const mainAreaRef = useRef(null);

  const [flash, setFlash] = useState(null);
  const lastResultAtRef = useRef(undefined);
  const lastResultAt = game?.last_action_result?.at ?? null;
  useEffect(() => {
    if (!game) return undefined;
    if (lastResultAtRef.current === undefined || !lastResultAt || lastResultAt === lastResultAtRef.current) {
      lastResultAtRef.current = lastResultAt;
      return undefined;
    }
    lastResultAtRef.current = lastResultAt;
    setFlash(game.last_action_result);
    const t = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastResultAt, !!game]);

  // Online: refetch rules whenever a newer game version arrives (settings are server-wide).
  const rulesVersion = online ? game?.version ?? null : null;
  useEffect(() => {
    getSettings().then(setRules).catch(() => {});
  }, [settingsRefreshKey, rulesVersion]);
  const [setupChoiceMade, setSetupChoiceMade] = useState(false);
  // Sea Level Rise: board animation + summary (useSeaRise); the sound plays when the counter advances.
  const seaRise = useSeaRise(game);
  // Weather runs by itself: one timeline on every screen (server clock) — overlay, reveal, results.
  const weatherTL = useWeatherTimeline(game, faces);
  const lastSeenSlrEventsRef = useRef(0);
  const prevBonusesRef = useRef(null);
  const prevMoneyRef = useRef(null);
  // Refs used by the sound-effect side-effects. Every ref tracks the
  // previous game snapshot so we can play a sound only on transitions.
  const prevCellsRef = useRef(null);
  const prevTurnIndexRef = useRef(null);
  const prevGameOverRef = useRef(false);
  // Independent snapshot used by the coin animation engine (payments to the bank).
  const prevMoneyFxRef = useRef(null);
  const prevBankSeqRef = useRef(undefined);
  const [pendingIncome, setPendingIncome] = useState({});
  // Last round-income seq seen (undefined until the first snapshot).
  const prevIncomeSeqRef = useRef(undefined);
  const [muted, toggleMuted] = useMuted();
  // demolishState shape: { cellKey, cell, preview, error } | null

  const refresh = useCallback(async () => {
    if (online) {
      await reload();
      setLoading(false);
      return;
    }
    try {
      const g = await getGame(gameId);
      setGame(g);
    } catch (e) {
      if (e?.response?.status === 410 && e.response.data?.detail?.code === "old_version") {
        setOldVersion(e.response.data.detail);
        return;
      }
      const code = e?.response?.status === 401 ? e.response.data?.detail?.lobby_code : null;
      if (code) {
        navigate(`/join/${code}`);
        return;
      }
      toast.error("Game not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [gameId, navigate, online, reload, setOldVersion]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Weather deck faces / card counts / removals (again at each weather phase: the counts are a setting).
  const inWeatherPhase = game?.phase === "weather";
  useEffect(() => {
    getWeatherFaces()
      .then((d) => setFaces(d))
      .catch(() => {});
  }, [inWeatherPhase, settingsRefreshKey]);

  // Card art: the 6 weather faces + the weather / global backs when the main game starts; the
  // global card as soon as it is drawn (it is shown right after the weather card).
  const inMainGame = game?.mode === "main";
  useEffect(() => {
    if (inMainGame) preloadWeatherArt();
  }, [inMainGame]);
  const drawnGlobal = game?.weather_state?.global_card || null;
  useEffect(() => {
    if (drawnGlobal) preloadImage(globalCardSrc(drawnGlobal));
  }, [drawnGlobal?.no]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Sea Level Rise sound — fires whenever the `slr_events` counter advances.
  useEffect(() => {
    if (!game) return;
    const current = game.slr_events || 0;
    const seen = lastSeenSlrEventsRef.current;
    if (current > seen && game.last_slr_summary) S.slrRise();
    lastSeenSlrEventsRef.current = current;
  }, [game?.slr_events, game?.last_slr_summary]);

  // Real-time connectivity recompute — runs after every state change.
  useEffect(() => {
    if (!game?.id) return;
    // Money-delta toast — fires once we have a baseline.
    const prevMoney = prevMoneyRef.current;
    const nextMoney = {};
    for (const p of game.players) nextMoney[p.color] = p.money ?? 0;
    if (prevMoney && game.mode !== "setup") {
      for (const color of Object.keys(nextMoney)) {
        const before = prevMoney[color] ?? nextMoney[color];
        const after = nextMoney[color];
        const diff = after - before;
        if (diff > 0) {
          toast.success(`${color.toUpperCase()} earned +${diff} money`, {
            duration: 2500,
            icon: <Coin size={16} />,
          });
          // One ping per coin while inside a weather resolution; a single
          // bright chime for any other economy event (card effects,
          // endgame bonuses, etc.).
          if (game.weather_state) {
            for (let i = 0; i < diff; i++) {
              setTimeout(() => S.coinPing(), i * 90);
            }
          } else {
            S.coinChime();
          }
        }
      }
    }
    prevMoneyRef.current = nextMoney;

    let cancelled = false;
    if (game.mode === "main") {
      getScoring(game.id)
        .then((d) => {
          if (cancelled) return;
          setScores(Object.fromEntries(d.breakdowns.map((b) => [b.color, b.total])));
          setResiliences(Object.fromEntries(d.breakdowns.map((b) => [b.color, b.resilience])));
        })
        .catch(() => {});
    }
    getConnectivity(game.id)
      .then((d) => {
        if (cancelled) return;
        const prev = prevBonusesRef.current;
        const next = d.bonuses;
        if (prev) {
          for (const color of Object.keys(next || {})) {
            // CONNECTIVITY: building groups add population.
            for (const t of ["population"]) {
              const before = prev?.[color]?.[t] ?? 0;
              const after = next?.[color]?.[t] ?? 0;
              if (after > before) {
                toast.success(
                  `${color.toUpperCase()} Building group formed — ${t} bonus ${before} → ${after}`,
                );
              } else if (after < before) {
                toast(
                  `${color.toUpperCase()} Building group broken — ${t} bonus ${before} → ${after}`,
                );
              }
            }
          }
        }
        prevBonusesRef.current = next;
        setConnectivity(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [game]);

  // ------------------------------------------------------------------
  // SOUND SIDE-EFFECTS
  //
  // These effects observe game-state transitions and fire programmatic
  // sounds via the shared audio engine. They are strictly read-only —
  // nothing here mutates game state.
  // ------------------------------------------------------------------

  // ------------------------------------------------------------------
  // ANIMATED COIN SIDE-EFFECTS
  //
  // Every time the game state changes, diff players against the previous
  // snapshot: payments (cards, repairs) fly coins to the bank; round
  // income flies coins from the bank. Purely presentational.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!game?.cells || !game?.players) return;

    const bankSel = '[data-drop-bank="true"]';

    // ---- Round income: coins fly from the bank to each player ----
    const income = game.last_income;
    const prevIncomeSeq = prevIncomeSeqRef.current;
    if (prevIncomeSeq !== undefined && income?.seq && income.seq !== prevIncomeSeq) {
      const owed = {};
      for (const [color, inc] of Object.entries(income.players || {})) {
        const gained = (inc.total || 0) - (inc.debt_paid || 0);
        if (gained > 0) owed[color] = gained;
      }
      if (Object.keys(owed).length) {
        setPendingIncome((prev) => {
          const next = { ...prev };
          for (const [c, n] of Object.entries(owed)) next[c] = (next[c] || 0) + n;
          return next;
        });
        Object.entries(owed).forEach(([color, n], idx) => {
          const start = 350 + idx * 220;
          const flightMs = 900;
          const staggerMs = 110;
          setTimeout(() => {
            flyCoins({ from: bankSel, to: `[data-testid="player-money-${color}"]`, count: n, stagger: staggerMs, flight: flightMs, size: 15 });
          }, start);
          setTimeout(() => {
            setPendingIncome((prev) => {
              const next = { ...prev };
              const left = (next[color] || 0) - n;
              if (left <= 0) delete next[color];
              else next[color] = left;
              return next;
            });
          }, start + (Math.min(n, 6) - 1) * staggerMs + flightMs);
        });
      }
    }
    prevIncomeSeqRef.current = income?.seq ?? 0;

    const snapshot = () => ({
      moneyByColor: Object.fromEntries(game.players.map((p) => [p.color, p.money ?? 0])),
    });

    // CLIMATE BANK: a pay-in flies to the bank panel's balance (not to the market pile).
    const bankSeq = game.climate_bank?.seq ?? 0;
    const payNote = prevBankSeqRef.current !== undefined && bankSeq !== prevBankSeqRef.current && game.climate_bank?.last?.kind === "pay"
      ? game.climate_bank.last : null;
    prevBankSeqRef.current = bankSeq;

    const prev = prevMoneyFxRef.current;
    if (!prev) {
      prevMoneyFxRef.current = snapshot();
      return;
    }

    // ---- Player-level diffs (card payments) ----
    for (const player of game.players) {
      const before = prev.moneyByColor[player.color] ?? 0;
      const after = player.money ?? 0;
      const delta = after - before;
      if (delta < 0) {
        const paid = -delta;
        const playerSel = `[data-testid="player-money-${player.color}"]`;
        const toBankPanel = payNote && payNote.color === player.color && document.querySelector('[data-testid="bank-balance"]');
        flyCoins({
          from: playerSel,
          to: toBankPanel ? '[data-testid="bank-balance"]' : bankSel,
          count: paid,
          stagger: 150,
          flight: 2400,
          size: 15,
        });
      }
    }

    prevMoneyFxRef.current = snapshot();
  }, [game]);

  // Cell diff -> placement sounds.
  useEffect(() => {
    if (!game?.cells) return;
    const prev = prevCellsRef.current;
    if (prev) {
      for (const key of Object.keys(game.cells)) {
        const p = prev[key] || {};
        const n = game.cells[key];
        if (!n) continue;

        // New tile placed on an empty cell.
        if (!p.kind && n.kind === "tile") {
          const k = n.card_key;
          if (GREEN_SOUND_KEYS.has(k)) S.placeGreenTile();
          else if (BLUE_SOUND_KEYS.has(k)) S.placeBlueTile();
          else S.placeGray();
        }

        // Cube stack grew -> a House Cube dropped.
        const pStack = (p.cube_stack || []).length;
        const nStack = (n.cube_stack || []).length;
        if (n.kind === "cube" && nStack > pStack) {
          S.placeBuildingFloor();
        }

        // Center token appeared on top of a cube stack.
        if (!p.token && n.token) {
          const k = n.token.card_key;
          if (GREEN_SOUND_KEYS.has(k)) S.placeGreenToken();
          else if (BLUE_SOUND_KEYS.has(k)) S.placeBlueToken();
          else S.placeToken();
        }

        // New edge tokens added.
        const pTok = (p.edge_tokens || []).length;
        const nTok = (n.edge_tokens || []).length;
        if (nTok > pTok) {
          const added = (n.edge_tokens || []).slice(pTok);
          added.forEach((t, i) => {
            const k = t?.card_key;
            const playIt = () => {
              if (GREEN_SOUND_KEYS.has(k)) S.placeGreenToken();
              else if (BLUE_SOUND_KEYS.has(k)) S.placeBlueToken();
              else S.placeToken();
            };
            if (i === 0) playIt();
            else setTimeout(playIt, i * 120);
          });
        }
      }
    }
    prevCellsRef.current = game.cells;
  }, [game?.cells]);

  // Weather sounds: the card flips and the face's sound once the card is shown (useWeatherTimeline).

  // Protection-block notices -> toast every new entry.
  const seenBlocksRef = useRef(0);
  useEffect(() => {
    const ws = game?.weather_state;
    if (!ws) {
      seenBlocksRef.current = 0;
      return;
    }
    const blocks = ws.block_notices || [];
    blocks.slice(seenBlocksRef.current).forEach((msg) => toast(msg));
    seenBlocksRef.current = blocks.length;
  }, [game?.weather_state]);

  // Water resolution summary on each hit player's panel for 3 s, once the weather event finishes.
  const [waterFlash, setWaterFlash] = useState(null);
  const waterSeqRef = useRef(null);
  const waterTimerRef = useRef(null);
  useEffect(() => {
    if (!game) return;
    const seq = game.water_summary?.seq || 0;
    if (waterSeqRef.current === null) { waterSeqRef.current = seq; return; }
    if (seq <= waterSeqRef.current || game.phase === "weather") return;
    waterSeqRef.current = seq;
    setWaterFlash(game.water_summary.results || {});
    clearTimeout(waterTimerRef.current);
    waterTimerRef.current = setTimeout(() => setWaterFlash(null), 3000);
  }, [game?.water_summary?.seq, game?.phase]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(waterTimerRef.current), []);

  // Turn change -> soft click.
  useEffect(() => {
    if (!game || game.mode === "setup") {
      prevTurnIndexRef.current = game?.current_player_index ?? null;
      return;
    }
    const prev = prevTurnIndexRef.current;
    if (prev !== null && prev !== game.current_player_index) {
      S.turnClick();
    }
    prevTurnIndexRef.current = game.current_player_index;
  }, [game?.current_player_index, game?.mode]);

  // Game over -> warm resolution tone.
  useEffect(() => {
    if (game?.game_over && !prevGameOverRef.current) {
      S.gameEnd();
      setFinalOpen(true);
    }
    prevGameOverRef.current = !!game?.game_over;
  }, [game?.game_over]);


  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  // A player's turn ended (next player, or the round moved on to weather / sea rise).
  useEffect(() => {
    if (!game) return;
    const cur = { idx: game.current_player_index, phase: game.phase, mode: game.mode, color: game.players[game.current_player_index]?.color };
    const prev = prevTurnRef.current;
    prevTurnRef.current = cur;
    if (!prev || prev.mode !== "main" || cur.mode !== "main" || game.game_over) return;
    const inTurn = (ph) => ph === "action" || ph === "draw";
    if (inTurn(prev.phase) && (cur.idx !== prev.idx || !inTurn(cur.phase))) {
      setTurnDone({ color: prev.color, at: Date.now() });
    }
  }, [game]);
  useEffect(() => {
    if (!turnDone) return undefined;
    const t = setTimeout(() => setTurnDone(null), 2000);
    return () => clearTimeout(t);
  }, [turnDone]);

  // Seat mode: other screens may move the game on, so poll for changes.
  useEffect(() => {
    if (!seatPref || online) return undefined;
    let stopped = false;
    const id = setInterval(async () => {
      if (busyRef.current) return;
      const seq = actionSeqRef.current;
      try {
        const g = await getGame(gameId);
        if (stopped || seq !== actionSeqRef.current || busyRef.current) return;
        setGame((prev) => (prev && JSON.stringify(prev) === JSON.stringify(g) ? prev : g));
      } catch {
        /* ignore */
      }
    }, 2500);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [seatPref, gameId, online]);

  const activePlayer = useMemo(
    () => (game ? game.players[game.current_player_index] : null),
    [game],
  );
  // "This screen plays as": online always the viewer's colour; Pass & Play the saved seat (null = shared).
  const seat = online
    ? viewer?.my_color ?? null
    : seatPref && game?.players.some((p) => p.color === seatPref) ? seatPref : null;
  // The hand on this screen: mine when seated, else the active player's.
  const handPlayer = (seat && game?.players.find((p) => p.color === seat)) || activePlayer;

  const isSetup = game?.mode === "setup";
  const uiPhase = game?.phase;
  const totalCubesPlaced = useMemo(() => {
    if (!game) return 0;
    return (
      game.cubes_per_player * game.players.length -
      game.players.reduce(
        (sum, p) => sum + (p.starting_cubes_remaining ?? 0),
        0,
      )
    );
  }, [game]);
  const totalCubesNeeded = game
    ? game.cubes_per_player * game.players.length
    : 0;

  const clearSelection = () => {
    setSelectedCardIdx(null);
    setSelectedCell(null);
    setSelectedActionId(null);
  };

  const wrap = async (fn) => {
    try {
      setBusy(true);
      const next = await fn();
      actionSeqRef.current += 1;
      accept(next);
      clearSelection();
    } catch (e) {
      if (online) fail(e, "Action failed");
      else toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const onPlaceStartingCube = () => {
    if (!selectedCell)
      return toast.error("Pick a cell to place your starting cube");
    wrap(() => placeStartingCube(gameId, selectedCell));
  };

  const onRandomizeSetup = () => {
    setSetupChoiceMade(true);
    wrap(() => randomizeSetup(gameId)).then(() => {
      toast.success("Starting cubes placed — jump to Round 1");
    });
  };

  const onManualSetup = () => setSetupChoiceMade(true);

  // `edge` (TERRAIN 2 of 3): the Sea Wall's edge of the square, from the board's edge picker.
  const onPlayBuild = async (edgeArg) => {
    const edge = typeof edgeArg === "string" ? edgeArg : null;
    if (selectedCardIdx === null) return toast.error("Pick a Build card first");
    if (!selectedCell) return toast.error("Pick an empty cell to build on");
    const card = handPlayer.hand[selectedCardIdx];
    if (card.kind !== "build")
      return toast.error("Selected card is not a Build card");
    S.cardSelect();
    const entry = previewOn ? buildPreview?.cells?.[selectedCell] : null;
    const src = document.querySelector('[data-testid="placement-preview-card"]')
      || document.querySelector(`[data-testid="cell-${selectedCell.replace(",", "-")}"]`);
    const color = activePlayer.color;
    if (WALL_KEYS.has(card.key) && !edge) return toast.error("Click one of the 4 edges of the square");
    await wrap(() => playBuild(gameId, selectedCardIdx, selectedCell, edge));
    const target = [...document.querySelectorAll(
      `[data-testid="player-score-${color}"], [data-testid="mobile-score-${color}"]`,
    )].find((el) => el.offsetParent !== null);
    if (entry?.legal && src && target) {
      const a = src.getBoundingClientRect();
      const b = target.getBoundingClientRect();
      const d = entry.resilience_delta ?? 0;
      const from = toStage(a.left, a.top);
      setScoreFly({
        from: { x: from.x + 8, y: from.y + 4 },
        to: toStage(b.left, b.top),
        text: `Resilience ${d > 0 ? "+" : ""}${d}`,
      });
    }
  };

  const selectedBuildCard =
    selectedCardIdx !== null && handPlayer?.hand?.[selectedCardIdx]?.kind === "build"
      ? handPlayer.hand[selectedCardIdx]
      : null;
  const previewActive = !!selectedBuildCard && uiPhase === "action" && !isSetup && !game?.game_over;
  const previewOn = previewActive && buildPreview?.enabled && buildPreview.card_key === selectedBuildCard?.key;
  const previewCell = hoverCell ?? selectedCell;
  // Legal squares for the selected Build card always come from the server's placement check.
  const legalCells = useMemo(() => {
    if (!selectedBuildCard || buildPreview?.card_key !== selectedBuildCard.key) return null;
    return new Set(Object.entries(buildPreview.cells || {}).filter(([, v]) => v.legal).map(([k]) => k));
  }, [selectedBuildCard, buildPreview]);

  useEffect(() => {
    if (!previewActive) {
      setBuildPreview(null);
      setHoverCell(null);
      return;
    }
    let cancelled = false;
    getBuildPreview(gameId, selectedCardIdx)
      .then((d) => !cancelled && setBuildPreview(d))
      .catch(() => !cancelled && setBuildPreview(null));
    return () => {
      cancelled = true;
    };
  }, [game, selectedCardIdx, previewActive, gameId, settingsRefreshKey]);

  const onDiscard = () => {
    if (selectedCardIdx === null) return toast.error("Pick a card to discard");
    S.cardSelect();
    wrap(() => discardCard(gameId, selectedCardIdx));
  };

  const onEndTurn = () => {
    S.turnClick();
    wrap(() => endTurn(gameId));
  };

  const onDrawMarket = (idx) => {
    S.cardDraw();
    setViewed(null);
    wrap(() => drawCard(gameId, "market", idx));
  };
  // PAY UPFRONT: the blind card is shown first (SELECTED CARD + "Pay N"), or goes to the
  // discard pile when it can't be paid ("Can't afford: discarded").
  const onDrawDeck = () => {
    setViewed(null);
    setHoveredId(null);
    wrap(() => drawDeck(gameId));
  };
  const onPayBlind = () => {
    S.cardDraw();
    const card = game?.blind_card;
    wrap(async () => {
      const next = await payBlind(gameId);
      if (card) toast(`You paid ${cardCost(card)} for ${card.name}.`, { duration: 2400 });
      return next;
    });
  };
  const onSkipPick = () => {
    S.cardSelect();
    setViewed(null);
    wrap(() => skipPick(gameId));
  };
  const onDiscardActionCard = (index) => {
    S.cardSelect();
    const limit = rules?.foundation?.action_discards_per_game ?? 2;
    const used = (handPlayer?.action_discards ?? 0) + 1;
    wrap(async () => {
      const next = await discardActionCard(gameId, index);
      toast(`Action card discarded (${Math.max(0, limit - used)} left this game).`, { duration: 2400 });
      return next;
    });
  };
  const onTakeAction = () => {
    S.cardDraw();
    wrap(async () => {
      const next = await takeActionCard(gameId);
      toast(`Action card taken for ${limits.actionPrice}. It lies face down beside you.`, { duration: 2200 });
      return next;
    });
  };
  const onWipeMarket = () => {
    S.cardSelect();
    setViewed(null);
    wrap(() => wipeMarket(gameId));
  };

  // A late "Yes" is refused by the server ("Too late — ... you paid the repairs"):
  // the decline is already saved, so show the reason and load the real state.
  const onWeatherProtectionDecide = async (play) => {
    try {
      setBusy(true);
      const next = await weatherProtectionDecide(gameId, play);
      actionSeqRef.current += 1;
      accept(next);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === "string" && msg.startsWith("Too late")) {
        toast.error(msg, { duration: 5000 });
        await refresh();
      } else if (online) fail(e, "Action failed");
      else toast.error(msg || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  // The results hold ran out (server clock): ask the server, which ends the round once.
  const onWeatherDeadline = () => {
    if (!busyRef.current) refresh();
  };

  // ------------------------------------------------------------------
  // Demolition (Board Mechanics module)
  //
  // Flow: player clicks Demolish -> enters `demolishMode` -> board shows
  // X overlays on the active player's own squares; clicking one
  // opens the preview modal. Cancelling or confirming exits the mode.
  // ------------------------------------------------------------------
  const [demolishMode, setDemolishMode] = useState(false);
  const [blindSeen, setBlindSeen] = useState(null); // PAY UPFRONT: last "Can't afford: discarded" dismissed
  const [fallingCell, setFallingCell] = useState(null);
  const [seaPulse, setSeaPulse] = useState(false);
  // Pulse the sea band once when a Sea Level Rise becomes pending.
  const slrWasPending = useRef(null);
  useEffect(() => {
    const now = !!game?.slr_pending;
    if (slrWasPending.current === false && now) {
      setSeaPulse(true);
      const t = setTimeout(() => setSeaPulse(false), 450);
      slrWasPending.current = now;
      return () => clearTimeout(t);
    }
    if (game) slrWasPending.current = now;
    return undefined;
  }, [game]);
  // Esc cancels a selected card (not while an action card or zoom is open).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || zoom || showRulebook) return;
      // Esc always clears the card detail (hand / market card or board square); it also cancels a selected card (not an action card in progress).
      if (viewed) setViewed(null);
      if (tileView) setTileView(null);
      if (selectedCardIdx === null || game?.action_card_state) return;
      setSelectedCardIdx(null);
      setSelectedCell(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedCardIdx, game, zoom, showRulebook, viewed, tileView]);
  // Reading a board square gives way to anything that uses the slot or the board clicks.
  const acActive = !!game?.action_card_state;
  useEffect(() => {
    if (viewed || selectedCardIdx !== null || demolishMode || acActive) setTileView(null);
  }, [viewed, selectedCardIdx, demolishMode, acActive]);
  // A new turn starts with an empty card detail.
  useEffect(() => {
    setViewed(null);
    setSelectedActionId(null);
  }, [game?.current_player_index]);
  // Market wipe (paid or automatic): one short notice.
  const wipeSeqRef = useRef(undefined);
  const wipeSeq = game?.last_market_wipe?.seq ?? 0;
  useEffect(() => {
    if (!game) return;
    if (wipeSeqRef.current !== undefined && wipeSeq > wipeSeqRef.current) {
      const w = game.last_market_wipe;
      const who = w.by ? game.players.find((p) => p.color === w.by)?.name : null;
      toast(w.auto ? "3 of the same card: the market was wiped for free." : `${who || "A player"} wiped the market.`, { duration: 2500 });
    }
    wipeSeqRef.current = wipeSeq;
  }, [wipeSeq, !!game]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDemolishableTile = useMemo(() => {
    if (!game || !activePlayer || game.mode === "setup") return false;
    if (game.phase !== "action") return false;
    for (const cell of Object.values(game.cells || {})) {
      if (!cell || cell.owner !== activePlayer.color) continue;
      if (!cell.kind) continue;
      if (cell.kind === "sea_marker") continue;
      return true;
    }
    return false;
  }, [game, activePlayer]);

  const isDemolishTargetCell = useCallback(
    (cellKey) => {
      const cell = game?.cells?.[cellKey];
      if (!cell || !activePlayer) return false;
      if (cell.owner !== activePlayer.color) return false;
      if (!cell.kind) return false;
      if (cell.kind === "sea_marker") return false;
      return true;
    },
    [game, activePlayer],
  );

  const onRequestDemolish = () => {
    if (!hasDemolishableTile) return;
    setSelectedCell(null);
    setSelectedCardIdx(null);
    setDemolishMode(true);
  };

  const onDemolishSelectCell = async (cellKey) => {
    if (!isDemolishTargetCell(cellKey)) return;
    const cell = game.cells[cellKey];
    try {
      setBusy(true);
      const preview = await demolishPreview(gameId, cellKey);
      setDemolishState({
        cellKey,
        cell,
        preview,
        error: null,
      });
    } catch (e) {
      if (isTableError(e)) {
        setDemolishMode(false);
        fail(e, "Cannot demolish this square");
        return;
      }
      setDemolishState({
        cellKey,
        cell,
        preview: null,
        error: e?.response?.data?.detail || "Cannot demolish this square",
      });
    } finally {
      setBusy(false);
    }
  };

  const onConfirmDemolish = async () => {
    if (!demolishState) return;
    try {
      setBusy(true);
      const next = await demolish(gameId, demolishState.cellKey);
      setDemolishState(null);
      setDemolishMode(false);
      // On-screen only: the whole square slides down, then the server state lands.
      setFallingCell(demolishState.cellKey);
      await new Promise((res) => setTimeout(res, 180));
      setFallingCell(null);
      actionSeqRef.current += 1;
      accept(next);
      clearSelection();
      toast.success(
        `Demolished ${demolishState.cellKey} for ${demolishState.preview?.total_cost ?? 0} money`,
      );
    } catch (e) {
      if (isTableError(e)) {
        setDemolishState(null);
        setDemolishMode(false);
        fail(e, "Demolition failed");
        return;
      }
      const msg = e?.response?.data?.detail || "Demolition failed";
      setDemolishState((s) => (s ? { ...s, error: msg } : s));
    } finally {
      setBusy(false);
    }
  };

  const onCancelDemolish = () => {
    setDemolishState(null);
    setDemolishMode(false);
  };

  // ------------------------------------------------------------------
  // Action Cards (interactive flow)
  // ------------------------------------------------------------------
  const acState = game?.action_card_state ?? null;

  // Compute which cells should be highlighted based on the current
  // action card step. Returns an array of "r,c" keys, or null if the
  // current step doesn't drive cell selection.
  const actionCardCellTargets = acState?.payload?.highlight || null;
  const actionCardChosen = acState?.payload?.chosen || [];

  const onActionSelectCell = (cell, edge = null) =>
    wrap(() => actionCardSelectCell(gameId, cell, edge));
  const onActionCancel = () => {
    setViewed(null);
    return wrap(() => actionCardCancel(gameId));
  };
  const onActionBack = () => wrap(() => actionCardBack(gameId));
  const onActionConfirm = () => wrap(() => actionCardConfirm(gameId));
  // Selecting an action card starts targeting straight away (cancelling any other card in progress).
  const startActionCard = (i) =>
    wrap(async () => {
      if (acState) await actionCardCancel(gameId);
      return playAction(gameId, i);
    });
  // CLIMATE BANK (v3 step 5): pay in / propose (the vote is answered through onResponseDecide).
  const onBankPay = (amount) => wrap(() => bankPay(gameId, amount));
  const onBankPropose = (project) => wrap(() => bankPropose(gameId, project));
  const onResponseDecide = useCallback(
    (yes, itemId) => wrap(() => responsesDecide(gameId, yes, itemId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gameId],
  );

  if (oldVersion) {
    return <OldVersionNotice detail={oldVersion} onNewGame={() => navigate(oldVersion.online ? "/online" : "/local")} />;
  }

  if (loading || !game) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="loading-state"
      >
        <div className="label-kicker text-[color:var(--ink-3)]" data-testid="loading-state-text">
          {online && reconnecting ? "Reconnecting…" : "Loading game…"}
        </div>
      </div>
    );
  }

  // One lock for both modes: online from viewer.can_act, Pass & Play from the seat picker.
  const canAct = !!viewer?.can_act;
  const gameOver = !!game.game_over;
  // Finished game: read-only everywhere (no turn controls, no hand actions).
  const myTurn = !gameOver && (online ? canAct : !seat || seat === activePlayer?.color);
  // TERRAIN 2 of 3 — the board's edge picker: a selected Sea Wall card on a chosen square of yours
  // (build on the clicked edge), or the edge step of Sabotage / Repair (server edge_options).
  const selCard = selectedCardIdx !== null && handPlayer ? handPlayer.hand[selectedCardIdx] : null;
  const wallPickCell = myTurn && !acState && !demolishMode && selCard && WALL_KEYS.has(selCard.key)
    && selectedCell && (!legalCells || legalCells.has(selectedCell)) && uiPhase === "action"
    ? selectedCell : null;
  const edgePicker = acState?.step === "choose_edge" && myTurn
    ? { cell: (acState.payload?.chosen || [])[0], options: acState.payload?.edge_options || [], onPick: (side) => onActionSelectCell((acState.payload?.chosen || [])[0], side) }
    : wallPickCell
      ? { cell: wallPickCell, options: wallEdgeOptions(game.walls, wallPickCell, handPlayer?.color, game.players), onPick: (side) => onPlayBuild(side) }
      : null;
  const onDownloadImage = async () => {
    try {
      setDownloading(true);
      const name = await downloadBoardImage(mainAreaRef.current);
      toast.success(`Saved ${name}`);
    } catch (e) {
      toast.error("Could not save the board image");
    } finally {
      setDownloading(false);
    }
  };
  const onDownloadLog = () => {
    const name = downloadGameLog(game);
    toast.success(`Saved ${name}`);
  };
  const actor = eventActor(game);
  const eventLocked = online ? !canAct : !!seat && !!actor && seat !== actor;
  const actorSeat = online ? viewer?.actor_seat ?? null : null;
  const waitingFor = online && !canAct && actorSeat !== null ? game.players[actorSeat]?.color ?? null : null;
  const skipPlayer =
    online && viewer?.is_leader && actorSeat !== null && (viewer.gone_seats || []).includes(actorSeat)
      ? game.players[actorSeat]
      : null;
  const isLeader = !online || !!viewer?.is_leader;
  const onLeaveOrNew = online
    ? () => {
        clearOnline();
        navigate("/");
      }
    : () => navigate("/local");
  const acOriginatorName = acState && game.players.find((p) => p.color === acState.originator_color)?.name;
  const acForOthers = online && !!acState && acState.originator_color !== seat;
  const onSeatChange = (color) => {
    clearSelection();
    setDemolishMode(false);
    setSeat(color);
  };

  // Instruction bar + Focus dimming (opacity only; dimmed areas stay clickable).
  const guide = computeGuidance({
    game,
    seat,
    online,
    uiPhase,
    selectedCard: selectedCardIdx !== null ? handPlayer?.hand?.[selectedCardIdx] ?? null : null,
    selectedCell,
    selectedAction: (handPlayer?.action_cards || []).find((c) => c.id === selectedActionId) ?? null,
    acState,
    demolishMode,
    seaSummary: seaRise.summary,
    turnDone,
    waitingFor,
    drawing: game.phase === "weather" && !weatherTL.resultsShown,
  });
  const dimOn = focusOn && !!guide?.focus && !guide.muted;
  const lit = (region) => !dimOn || guide.focus.includes(region);
  const regionStyle = (region) => ({ opacity: lit(region) ? 1 : 0.5 });
  const modalFocus = focusOn && guide && !guide.muted ? guide.modal : null;
  // PRIVATE RESPONSE WINDOW (Board Thief block / Mangrove prompt) takes the SELECTED CARD area.
  const respPrompt = !!game.response_state;
  const eventInPanel = (game.phase === "weather" && !isSetup) || !!seaRise.summary;
  const weatherStyle = eventInPanel ? { opacity: lit("modal") || lit("summary") ? 1 : 0.5 } : regionStyle("players");
  // Market rule: before the action the market + deck are dimmed; taking opens after the action.
  const marketRuleDim = game.mode === "main" && uiPhase === "action" && myTurn && !game.game_over;

  const showAllHands = !online && !!rules?.display?.show_all_hands;
  const canSelectCard = !isSetup && uiPhase === "action" && myTurn;
  const canDrawMarket = game.mode === "main" && uiPhase === "draw" && !busy && myTurn;
  const deckAvailable = (game.deck_size ?? game.deck?.length ?? 0) + (game.discard_pile?.length ?? 0) > 0;
  const canDrawDeck = canDrawMarket && deckAvailable;
  const ownTurnAction = canSelectCard && game.phase === "action";
  // Card detail: a click shows the card; clicking the same card again clears it.
  const onHandClick = (card, i) => {
    const same = viewed?.id === card.id;
    const view = () => setViewed(same ? null : { id: card.id });
    if (!canSelectCard) return view();
    if (card.kind === "action" && !RESPONSE_NOTES[card.key]) {
      // Selecting an action card starts its flow directly.
      if (!ownTurnAction || acState?.card_id === card.id) return view();
      setViewed({ id: card.id });
      S.cardSelect();
      setSelectedCardIdx(null);
      startActionCard(i);
      return undefined;
    }
    if (i === selectedCardIdx) {
      setSelectedCardIdx(null);
      setSelectedCell(null);
      setViewed(null);
      return undefined;
    }
    setViewed({ id: card.id });
    if (acState) wrap(() => actionCardCancel(gameId));
    S.cardSelect();
    setSelectedActionId(null);
    setSelectedCardIdx(i);
    return undefined;
  };
  // Held action cards (owner only, face up in the player panel): click shows + selects for "Play action card".
  const ownActions = handPlayer?.action_cards || [];
  const selectedAction = ownActions.find((c) => c.id === selectedActionId) ?? null;
  const onActionCardClick = (card) => {
    const same = viewed?.id === card.id;
    setViewed(same && !ownTurnAction ? null : { id: card.id });
    if (!ownTurnAction || acState) return;
    if (RESPONSE_NOTES[card.key]) {
      toast(RESPONSE_NOTES[card.key], { duration: 2500 });
      return;
    }
    S.cardSelect();
    setSelectedCardIdx(null);
    setSelectedCell(null);
    setSelectedActionId(selectedActionId === card.id ? null : card.id);
  };
  const onPlayAction = () => {
    const playable = ownActions.filter((c) => !RESPONSE_NOTES[c.key]);
    const card = selectedAction || (playable.length === 1 ? playable[0] : null);
    if (!card) return toast.error("Click one of your action cards first");
    const i = ownActions.findIndex((c) => c.id === card.id);
    setViewed({ id: card.id });
    S.cardSelect();
    startActionCard(i);
    return undefined;
  };
  const hand = handPlayer?.hand || [];
  const marketViewIdx = viewed ? game.market.findIndex((c) => c.id === viewed.id) : -1;
  const hoveredHandCard = hoveredId ? hand.find((c) => c.id === hoveredId) || ownActions.find((c) => c.id === hoveredId) : null;
  const hoveredMarketCard = hoveredId && !hoveredHandCard ? game.market.find((c) => c.id === hoveredId) : null;
  // PAY UPFRONT: a blind-drawn card is shown here to its taker (then "Pay N"), or — when it
  // couldn't be paid — once with "Can't afford: discarded" until OK / another card is viewed.
  const blindPending = myTurn && game.blind_card && game.blind_owner === activePlayer?.color ? game.blind_card : null;
  const blindGone = !blindPending && myTurn && !viewed && game.blind_result?.outcome === "discarded"
    && game.blind_result.color === activePlayer?.color && blindSeen !== game.blind_result.seq ? game.blind_result : null;
  const blindShown = blindPending || (blindGone ? { ...blindGone.card, id: `blind-${blindGone.seq}` } : null);
  const slotCard =
    blindPending ||
    hoveredHandCard ||
    hoveredMarketCard ||
    (blindGone && blindShown) ||
    (marketViewIdx >= 0 && game.market[marketViewIdx]) ||
    (viewed && (hand.find((c) => c.id === viewed.id) || ownActions.find((c) => c.id === viewed.id))) ||
    null;
  const slotSource = blindShown && slotCard === blindShown ? "blind" : hoveredMarketCard || (!hoveredHandCard && marketViewIdx >= 0) ? "market" : "hand";
  // Board square being read (only when no hand / market card is shown).
  const tileSlot = !slotCard && tileView
    ? tileCardsFor(tileView.cell, game.cells?.[tileView.cell], game.players, rules?.cards?.build, game.grid_rows, heightOf(game.terrain, tileView.cell), game.walls)
    : null;
  const tileTab = tileSlot ? Math.min(tileView.tab || 0, Math.max(0, tileSlot.entries.length - 1)) : 0;
  const shownCard = slotCard || tileSlot?.entries[tileTab]?.card || null;
  const shownSource = slotCard ? slotSource : "board";
  // "Take this card" (under the large card) while the viewed market card is the one shown, during a pick.
  const canTakeShown = canDrawMarket && !flash && marketViewIdx >= 0 && shownCard?.id === game.market[marketViewIdx]?.id;
  // Board clicks read a square only while nothing else is waiting for a click.
  const readMode = !demolishMode && !demolishState && !actionCardCellTargets && selectedCardIdx === null && !(isSetup && myTurn);
  const onReadCell = (key) => {
    if (!key) return setTileView(null);
    setViewed(null);
    setTileView((t) => (t?.cell === key ? null : { cell: key, tab: 0 }));
    return undefined;
  };
  const zoomView = (card) => setZoom({ card });

  // ------------------------------------------------------------------
  // LAYOUT F: the buttons for the card shown live under the SELECTED CARD (CardActions);
  // END TURN is in the top bar, Wipe market in the marketplace, Take action card = the deck.
  // ------------------------------------------------------------------
  const expandedColor = activePlayer?.color ?? null;
  const turnPhase = !game.game_over && (isSetup || uiPhase === "action" || uiPhase === "draw");
  const controlsHere = turnPhase && myTurn && !acForOthers;
  const inDebt = (activePlayer?.debt ?? 0) > 0;
  const turn = { ...(game.turn || {}), demolitions: game.demolitions_this_turn ?? 0 };
  const limits = {
    discards: rules?.foundation?.discards_per_turn ?? 1,
    actionMax: rules?.foundation?.action_cards_max ?? 3,
    wipeCost: rules?.foundation?.market_wipe_cost ?? 2,
    demolitions: rules?.foundation?.demolitions_per_turn ?? 1,
    demolishFee: rules?.board_mechanics?.demolition_rules?.demolish_fee ?? 2,
    actionDiscards: rules?.foundation?.action_discards_per_game ?? 2,
    actionPrice: 3, // PAY UPFRONT: every action card costs 3, paid when taken
  };
  const mainTurn = controlsHere && game.mode === "main";
  const free = mainTurn && uiPhase === "action" && !busy && !acState && !flash && !game.response_state;
  const picking = mainTurn && uiPhase === "draw";
  const heldCount = activePlayer?.action_cards?.length ?? activePlayer?.action_card_count ?? 0;
  const actionPileEmpty = (game.action_deck_size ?? game.action_deck?.length ?? 0) + (game.action_discard_pile?.length ?? 0) === 0;
  const takeActionReason = !mainTurn
    ? null
    : picking
      ? "Take a market card first"
      : turn.took_action
        ? "Already took an action card this turn"
        : heldCount >= limits.actionMax
          ? `You hold ${limits.actionMax} action cards (the most you can hold)`
          : actionPileEmpty
            ? "The action pile is empty"
            : inDebt
              ? TAKE_DEBT_REASON
              : (activePlayer?.money ?? 0) < limits.actionPrice
                ? `${TAKE_MONEY_REASON} (it costs ${limits.actionPrice})`
                : null;
  const canTakeAction = free && !takeActionReason;
  // PAY UPFRONT — the pick: market cards are paid at once; a blind card is shown, then paid.
  const myMoney = activePlayer?.money ?? 0;
  // The pick is open on this screen (after a play or discard).
  const pickOpen = controlsHere && game.mode === "main" && uiPhase === "draw";
  const blindCard = pickOpen ? blindPending : null;
  const takeReason = (card) => (inDebt ? TAKE_DEBT_REASON : cardCost(card) > myMoney ? TAKE_MONEY_REASON : null);
  const anyAffordable = (game.market || []).some((c) => !takeReason(c));
  const deckReason = !pickOpen ? null : blindCard ? "Pay for the card you drew first" : inDebt ? TAKE_DEBT_REASON : null;
  const skip = pickOpen
    ? { enabled: !busy && !blindCard && !anyAffordable, reason: blindCard ? "Pay for the card you drew first" : anyAffordable ? "You can afford a market card" : null, onClick: onSkipPick }
    : null;
  const wipeReason = !mainTurn
    ? "Only on your turn"
    : inDebt
      ? "You are in debt"
      : blindCard
        ? "Pay for the card you drew first"
        : turn.wiped
        ? "Already wiped this turn"
        : (activePlayer?.money ?? 0) < limits.wipeCost
          ? `You need ${limits.wipeCost} coins`
          : null;
  const canWipe = (free || (picking && !busy)) && !wipeReason;

  // Which card is shown: a build card from this hand, a held action card, a market card, a board square.
  const shownHandIdx = shownCard ? hand.findIndex((c) => c.id === shownCard.id) : -1;
  const shownAction = shownCard ? ownActions.find((c) => c.id === shownCard.id) ?? null : null;
  const shownCell = tileSlot ? tileView.cell : null;
  const shownCellData = shownCell ? game.cells?.[shownCell] : null;
  const ownSquare = !!shownCellData?.kind && shownCellData.kind !== "sea_marker" && shownCellData.owner === activePlayer?.color;
  const sourceNote = shownSource === "blind"
    ? blindPending ? "Drawn blind: only you see it" : "Drawn blind"
    : shownHandIdx >= 0 || shownAction
    ? "From your hand"
    : shownSource === "market"
      ? "Marketplace"
      : shownCell
        ? `${shownCellData?.owner === seat || (!seat && ownSquare) ? "Your square" : "Square"} ${squareShort(shownCell, game.grid_rows)}`
        : null;

  const cardButtons = [];
  if (controlsHere && !isSetup && !flash) {
    if (shownHandIdx >= 0 && hand[shownHandIdx].kind === "build" && mainTurn) {
      const card = hand[shownHandIdx];
      const isSel = selectedCardIdx === shownHandIdx;
      const isWallCard = WALL_KEYS.has(card.key);
      const legal = isSel && !!selectedCell && (!legalCells || legalCells.has(selectedCell)) && !isWallCard;
      const buildPhase = uiPhase === "action";
      // PAY UPFRONT: a hand card is already paid for — playing it is free (in debt too).
      const playReason = !buildPhase ? "Take a market card first" : null;
      cardButtons.push({
        testid: "btn-play-build",
        // Short labels that always fit; the card's name is on the big card above.
        label: "Play",
        title: "Play this build card (already paid)",
        primary: true,
        enabled: legal && !playReason && !busy && !acState,
        reason: playReason || (legal ? null : isWallCard && isSel && selectedCell ? "Click one of the 4 edges of the square" : isWallCard ? "Pick your square, then one of its edges" : "Pick a square for this card"),
        onClick: onPlayBuild,
      });
      const discardsLeft = Math.max(0, limits.discards - (turn.discards ?? 0));
      cardButtons.push({
        testid: "btn-discard",
        label: `Discard (${discardsLeft} left)`,
        title: "Discard this build card",
        enabled: free && discardsLeft > 0 && isSel,
        reason: picking ? "Take a market card first" : discardsLeft <= 0 ? "No discards left this turn" : null,
        onClick: onDiscard,
      });
    } else if (shownAction && mainTurn) {
      const idx = ownActions.findIndex((c) => c.id === shownAction.id);
      const response = !!RESPONSE_NOTES[shownAction.key];
      // PAY UPFRONT: held action cards were paid 3 when taken — playing is free (in debt too).
      const playReason = response
        ? "Response card: played when triggered"
        : picking
          ? "Take a market card first"
          : turn.played_action
            ? "Already played an action card this turn"
            : null;
      cardButtons.push({
        testid: "btn-play-action",
        label: "Play",
        title: `Play ${shownAction.name || "this action card"} (already paid)`,
        primary: true,
        enabled: free && !playReason,
        reason: playReason,
        onClick: () => {
          setViewed({ id: shownAction.id });
          S.cardSelect();
          startActionCard(idx);
        },
      });
      const left = Math.max(0, limits.actionDiscards - (handPlayer?.action_discards ?? 0));
      cardButtons.push({
        testid: "btn-discard-action",
        label: `Discard (${left} left)`,
        title: `Discard this action card (${left} left this game)`,
        enabled: free && left > 0,
        reason: left <= 0 ? "No action card discards left this game" : picking ? "Take a market card first" : null,
        onClick: () => onDiscardActionCard(idx),
      });
    } else if (shownSource === "blind" && blindCard) {
      cardButtons.push({ testid: "btn-pay-blind", label: `Pay ${cardCost(blindCard)}`, title: `Pay ${cardCost(blindCard)} for ${blindCard.name}`, primary: true, enabled: !busy, onClick: onPayBlind });
    } else if (shownSource === "blind" && blindGone) {
      cardButtons.push({ testid: "btn-blind-discarded", label: "Can't afford: discarded", enabled: false, reason: `It costs ${blindGone.price}; it went to the discard pile.` });
      cardButtons.push({ testid: "btn-blind-ok", label: "OK", enabled: true, onClick: () => setBlindSeen(blindGone.seq) });
    } else if (shownSource === "market" && marketViewIdx >= 0 && (mainTurn || pickOpen)) {
      const card = game.market[marketViewIdx];
      const reason = !pickOpen ? "Play or discard a build card first" : takeReason(card);
      cardButtons.push({ testid: "market-take-btn", label: `Take (${cardCost(card)})`, title: `Take ${card.name} and pay ${cardCost(card)} now`, primary: true, enabled: canTakeShown && !busy && !reason && !blindCard, reason: reason || (blindCard ? "Pay for the card you drew first" : null), onClick: () => onDrawMarket(marketViewIdx) });
    } else if (shownCell && ownSquare && mainTurn) {
      const demoReason = picking
        ? "Take a market card first"
        : inDebt
          ? "You are in debt"
          : turn.demolitions >= limits.demolitions
            ? "Already demolished a square this turn"
            : null;
      cardButtons.push({
        testid: "btn-demolish",
        label: `Demolish (${limits.demolishFee})`,
        title: `Demolish this square (${limits.demolishFee})`,
        danger: true,
        enabled: free && !demoReason,
        reason: demoReason,
        onClick: () => onDemolishSelectCell(shownCell),
      });
    }
  }

  // Body under the large card: the running step (flash, demolish confirm, action card targeting,
  // Phase 1 cube) or the card's buttons.
  const slotBody = flash ? (
    <ActionResultFlash result={flash} />
  ) : demolishState && controlsHere ? (
    <DemolishConfirm cellKey={demolishState.cellKey} gridRows={game.grid_rows} cell={demolishState.cell} preview={demolishState.preview} error={demolishState.error} player={handPlayer} onConfirm={onConfirmDemolish} onCancel={onCancelDemolish} busy={busy} />
  ) : acState && !acForOthers ? (
    <ActionCardPanel state={acState} cells={game.cells} gridRows={game.grid_rows} busy={busy || !myTurn} onBack={onActionBack} onCancel={onActionCancel} onConfirm={onActionConfirm} />
  ) : acForOthers ? (
    <ActionCardWaiting name={acOriginatorName || "Someone"} />
  ) : isSetup && controlsHere ? (
    <div data-testid="action-bar" className="flex flex-col" style={{ gap: 4 }}>
      <button data-testid="btn-place-starting-cube" onClick={onPlaceStartingCube} disabled={busy || !selectedCell} className="btn-primary btn-compact w-full" style={{ height: 38 }}>
        Place starting cube
      </button>
      {!selectedCell && <span className="text-center" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>Pick a square on the board first</span>}
    </div>
  ) : (
    <CardActions buttons={cardButtons} />
  );

  const L = compact => (compact ? LAYOUT.compact : LAYOUT.wide);
  const evMode = !isSetup ? eventMode(game, seaRise.summary) : null;
  const endTurnVisible = !isSetup && !game.game_over && game.mode === "main" && (game.phase === "action" || game.phase === "draw");
  const endTurnReason = !myTurn
    ? null
    : uiPhase === "draw"
      ? game.blind_card ? "Pay for the card you drew first" : "Take a card first (or skip if you can't afford one)"
      : acState
        ? "Finish or cancel your action card first"
        : null;
  const pill = game.game_over
    ? null
    : game.phase === "weather"
      ? { color: null, text: "End of round" }
      : online && !canAct && !acForOthers
        ? { color: activePlayer?.color, text: `Waiting for ${activePlayer?.name || "…"}`, muted: true }
        : { color: activePlayer?.color, text: `Player ${game.current_player_index + 1} · ${myTurn ? "your turn" : `${activePlayer?.name}'s turn`}` };

  return (
    <Stage>
    {({ compact }) => (
    <div data-testid="game-layout" data-layout={compact ? "compact" : "wide"} className="relative h-full w-full flex flex-col overflow-hidden">
      <TopBar
        game={game}
        rules={rules}
        isSetup={isSetup}
        maxRounds={rules?.economy?.endgame?.max_rounds ?? 12}
        setupTitle={`Place your cubes (${totalCubesPlaced}/${totalCubesNeeded})`}
        setupText={`Phase 1 · Setup round ${game.setup_round} of ${game.cubes_per_player}`}
        guide={guide}
        pill={pill}
        endTurn={{ visible: endTurnVisible, enabled: myTurn && !busy && !endTurnReason && game.phase === "action" && !game.response_state, reason: game.response_state ? "Waiting for the response window" : endTurnReason, onClick: onEndTurn }}
        weatherChip={<WeatherChip game={game} faces={faces} />}
        muted={muted}
        onToggleMute={toggleMuted}
        onScore={() => setShowLiveScore(true)}
        onRulebook={() => setShowRulebook(true)}
        onSettings={designer ? () => setShowSettings(true) : undefined}
        onNewGame={onLeaveOrNew}
        logOpen={logOpen}
        onLogOpenChange={setLogOpen}
        focusOn={focusOn}
        onToggleFocus={toggleFocus}
        seat={seat}
        onSeatChange={online ? undefined : onSeatChange}
        online={online}
        skipName={skipPlayer?.name ?? null}
        onSkip={() => wrap(() => skipGoneSeat(gameId))}
        onShowScores={() => setFinalOpen(true)}
        onDownloadImage={onDownloadImage}
        onDownloadLog={onDownloadLog}
        downloading={downloading}
      />
      {reconnecting && (
        <div
          data-testid="reconnecting-banner"
          role="status"
          className="fixed left-1/2 -translate-x-1/2 z-[70]"
          style={{ top: 66, padding: "6px 14px", borderRadius: "var(--radius)", background: "var(--card)", boxShadow: "var(--shadow-card)", fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}
        >
          Reconnecting…
        </div>
      )}
      <div
        data-testid="main-area"
        ref={mainAreaRef}
        className="flex-1 min-h-0 grid overflow-hidden"
        style={{
          background: "var(--table)",
          padding: "10px 10px",
          columnGap: 12,
          alignItems: "start",
          gridTemplateColumns: `${L(compact).left}px minmax(0, 1fr) ${L(compact).right}px`,
          gridTemplateRows: "minmax(0, 1fr)",
        }}
      >
        <div data-testid="players-region" style={{ gridColumn: 1, gridRow: 1, minHeight: 0, height: "100%", display: "flex", overflow: "hidden" }} className="flex-col">
          <PlayersColumn
            game={game}
            scores={gameOver && game.final_scores ? game.final_scores : scores}
            resiliences={resiliences}
            pendingIncome={pendingIncome}
            showAllHands={showAllHands}
            onOpenScore={() => setShowLiveScore(true)}
            onZoomCard={zoomView}
            online={online ? { myColor: seat, canAct } : null}
            waterNotes={waterFlash}
            actionMax={limits.actionMax}
            expandedColor={expandedColor}
            infoStyle={regionStyle("players")}
          >
            <section data-testid="selected-card-area" data-mode={respPrompt ? "response" : evMode || "card"} className="flex-1 min-h-0 flex flex-col" style={{ marginTop: 10 }}>
              {respPrompt ? (
                <ResponseFlow game={game} busy={busy} focus={modalFocus} responder={{ online, seat }} onDecide={onResponseDecide} />
              ) : evMode ? (
                <EventPanel
                  game={game}
                  faces={faces}
                  style={weatherStyle}
                  seaSummary={seaRise.summary}
                  maxSlr={rules?.economy?.endgame?.slrs_to_end ?? 3}
                  onCloseSeaSummary={seaRise.closeSummary}
                  flow={{ timeline: weatherTL, onProtectionDecide: onWeatherProtectionDecide, onDeadline: onWeatherDeadline, busy, focus: modalFocus, responder: { online, seat } }}
                />
              ) : (
                <>
                  <div className="shrink-0 flex items-center whitespace-nowrap" style={{ height: 18, marginBottom: 6, gap: 8 }}>
                    <span className="section-label">Selected card</span>
                    {sourceNote && <span data-testid="selected-card-source" className="ml-auto truncate" style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{sourceNote}</span>}
                  </div>
                  <FitSlot layout={L(compact)}>
                    {({ width: slotW, regionH, emptyH }, regionRef) => (
                      <div ref={regionRef} data-testid="slot-region" data-slot-w={slotW} className="shrink-0 flex flex-col items-center" style={{ gap: 6, minHeight: regionH || undefined }}>
                        <LargeCardSlot card={shownCard} source={shownSource} note={!shownCard ? tileSlot?.note || null : null} detail={!slotCard ? tileSlot?.entries[tileTab]?.detail || null : null} width={slotW} emptyHeight={emptyH} onOpen={() => shownCard && zoomView(shownCard)} />
                        {tileSlot && (
                          <div data-testid="slot-caption" className="shrink-0" style={{ width: "100%" }}>
                            <TileCardTabs view={tileSlot} active={tileTab} width={slotW} tabsWidth={L(compact).left} onPick={(i) => setTileView((t) => (t ? { ...t, tab: i } : t))} />
                          </div>
                        )}
                        <div data-testid="slot-buttons" className="w-full shrink-0 empty:hidden" style={{ marginTop: "auto", ...regionStyle("controls") }}>{slotBody}</div>
                      </div>
                    )}
                  </FitSlot>
                </>
              )}
            </section>
          </PlayersColumn>
        </div>

        <section data-testid="centre-column" className="relative min-h-0 min-w-0 flex flex-col overflow-hidden" style={{ gridColumn: 2, gridRow: 1, height: "100%" }}>
          <BoardArea
            boardStyle={regionStyle("board")}
            cols={game.grid_cols}
            rows={game.grid_rows}
            renderBoard={(cellPx) => (
              <>
                <BoardGrid
                  cellPx={cellPx}
                  seaRise={seaRise.rise}
                  wallSplash={seaRise.splash}
                  gridCols={game.grid_cols}
                  gridRows={game.grid_rows}
                  floodView={game.flood_view}
                  walls={game.walls || {}}
                  edgePicker={edgePicker}
                  cells={game.cells}
                  terrain={game.terrain}
                  players={game.players}
                  legalCells={legalCells}
                  maxFloors={rules?.board_mechanics?.cube_rules?.max_floors}
                  selectedCell={selectedCell}
                  onSelectCell={(cell) => {
                    if (!myTurn) return;
                    if (demolishMode) {
                      onDemolishSelectCell(cell);
                      return;
                    }
                    if (acState && actionCardCellTargets?.includes(cell)) {
                      onActionSelectCell(cell);
                      return;
                    }
                    if (uiPhase !== "action") return;
                    setSelectedCell(cell === selectedCell ? null : cell);
                  }}
                  phase={uiPhase}
                  mode={game.mode}
                  selectedCard={selectedCardIdx !== null && handPlayer ? handPlayer.hand[selectedCardIdx] : null}
                  activeColor={online ? seat : activePlayer?.color}
                  startingCubeMode={isSetup}
                  actionTargets={actionCardCellTargets}
                  actionChosen={actionCardChosen}
                  demolishMode={demolishMode}
                  weather={game.phase === "weather" && weatherTL.boardArea ? game.weather_state : null}
                  weatherShuffle={weatherTL.shuffle}
                  thiefBanner={game.thief_banner}
                  round={game.round}
                  onHoverCell={previewOn ? setHoverCell : null}
                  illegalCell={previewOn && hoverCell && buildPreview.cells[hoverCell]?.legal === false ? hoverCell : null}
                  fallingCell={fallingCell}
                  readMode={readMode}
                  readCell={tileSlot ? tileView.cell : null}
                  onReadCell={onReadCell}
                />
                <ConnectivityOverlay gridCols={game.grid_cols} gridRows={game.grid_rows} terrain={game.terrain} cellPx={cellPx} edges={connectivity?.edges || []} components={connectivity?.components} />
                {previewOn && (
                  <PlacementPreview cellKey={previewCell} entry={buildPreview.cells[previewCell]} gridCols={game.grid_cols} gridRows={game.grid_rows} showTeaching={buildPreview.teaching} />
                )}
              </>
            )}
            renderCoast={(cellPx) => (
              <CoastalRow pulse={seaPulse} seaLevel={game.sea_level ?? game.slr_events ?? 0} maxLevel={rules?.economy?.endgame?.slrs_to_end ?? 3} />
            )}
          />
        </section>

        <aside data-testid="right-column" className="min-h-0 min-w-0 flex flex-col overflow-hidden" style={{ gridColumn: 3, gridRow: 1, height: "100%", gap: 10 }}>
          {gameOver ? (
            <FinalColumn game={game} />
          ) : (
          <>
          <MarketPanel
            market={game.market}
            mode={game.mode}
            cardW={L(compact).cardW}
            canDraw={canDrawMarket && !flash}
            viewedIdx={marketViewIdx >= 0 ? marketViewIdx : null}
            cardCost={cardCost}
            onView={(i) => {
              const id = game.market[i].id;
              setViewed((v) => (v?.id === id ? null : { id }));
            }}
            onHover={(card) => setHoveredId(card ? card.id : null)}
            canDrawDeck={canDrawDeck && !flash && !deckReason}
            deckReason={deckReason}
            onDrawDeck={onDrawDeck}
            pickHint={!pickOpen ? (mainTurn && inDebt ? `${TAKE_DEBT_REASON} until the debt is paid` : null) : blindCard ? `Pay ${cardCost(blindCard)} for the card you drew blind` : inDebt ? "You are in debt: you can't take cards. Skip the take." : !anyAffordable ? "No market card you can afford: draw blind or skip" : null}
            skip={skip}
            actionPrice={limits.actionPrice}
            deckCount={game.deck_size ?? game.deck.length}
            discardPile={game.discard_pile || []}
            onOpenDiscard={zoomView}
            actionCount={game.action_deck_size ?? game.action_deck?.length ?? 0}
            actionDiscardCount={game.action_discard_pile?.length ?? 0}
            canTakeAction={canTakeAction}
            takeActionReason={takeActionReason}
            onTakeAction={onTakeAction}
            onTakeBlocked={(reason) => reason && toast(reason, { duration: 2200 })}
            wipe={game.mode === "main" ? { cost: limits.wipeCost, enabled: canWipe, reason: wipeReason, onClick: onWipeMarket } : null}
            ruleDim={marketRuleDim}
            focusDim={!lit("market")}
            eventDecks={game.mode === "main" ? (
              <>
                <EventDeck kind="weather" label="Weather" testid="weather" dim={!lit("market")} count={deckLeft(game)}
                  title={[`Weather deck: ${deckLeft(game)} cards left, ${(game.weather_discard || []).length} in the discard`,
                    ...removalLines(game, faces)].join(String.fromCharCode(10))}
                  last={(() => {
                    const face = lastWeather(game, faces).face;   // the last weather drawn (the discard is emptied by each degree)
                    return face ? { src: weatherCardSrc(face.key), name: face.name, detail: face.description } : null;
                  })()} />
                <EventDeck kind="global" label="Global" testid="global" dim={!lit("market")} count={globalLeft(game)}
                  title={`Global deck: ${globalLeft(game)} cards left`}
                  last={game.last_global?.no ? { src: globalCardSrc(game.last_global), name: game.last_global.name, detail: globalEffectText(game.last_global) } : null} />
                <ClimateBankPanel game={game} player={activePlayer} enabled={free && !game.response_state} busy={busy} onPay={onBankPay} onPropose={onBankPropose} focusDim={!lit("bank")} />
              </>
            ) : null}
          />
          <HandRow
            player={handPlayer}
            seatNo={handPlayer ? game.players.findIndex((p) => p.color === handPlayer.color) + 1 : null}
            cardW={L(compact).cardW}
            actionMax={limits.actionMax}
            regionStyle={regionStyle("hand")}
            selectedCardIdx={myTurn ? selectedCardIdx : null}
            selectedActionId={selectedActionId}
            activeCardId={acState?.card_id}
            ownTurnAction={ownTurnAction}
            paid={game.mode === "main"}
            onCardClick={onHandClick}
            onActionClick={onActionCardClick}
            onCardHover={(card) => setHoveredId(card ? card.id : null)}
          />
          </>
          )}
        </aside>
      </div>

      <WeatherOverlay game={game} faces={faces} timeline={weatherTL} />

      <CardZoom zoom={zoom} onClose={closeZoom} actions={zoom && shownCard && zoom.card?.id === shownCard.id ? cardButtons : null} />


      {game.game_over && finalOpen && (
        <EndgameOverlay
          game={game}
          onClose={() => setFinalOpen(false)}
          onDownloadImage={onDownloadImage}
          onDownloadLog={onDownloadLog}
          downloading={downloading}
          canPlayAgain={isLeader}
          onError={online ? fail : undefined}
          onPlayAgain={(next) => {
            accept(next);
            setSelectedCardIdx(null);
            setSelectedCell(null);
          }}
        />
      )}

      <LiveScoreDialog game={game} open={showLiveScore} onOpenChange={setShowLiveScore} />
      <Rulebook open={showRulebook} onClose={closeRulebook} />
      {scoreFly && <ScoreFly fly={scoreFly} onDone={() => setScoreFly(null)} />}

      {showSettings && designer && (
        <SettingsPanel
          gameId={game.id}
          gameTestingMode={!!game.testing_mode}
          gameForcedWeather={game.forced_weather || null}
          readOnly={online}
          showTesting={isLeader}
          onError={online ? fail : undefined}
          onClose={() => setShowSettings(false)}
          onApplied={() => {
            setSettingsRefreshKey((k) => k + 1);
            refresh();
          }}
        />
      )}

      {isSetup && !setupChoiceMade && totalCubesPlaced === 0 && (!online || isLeader) && (
        <SetupChoice
          players={game.players}
          cubesPerPlayer={game.cubes_per_player}
          gridCols={game.grid_cols}
          gridRows={game.grid_rows}
          busy={busy}
          onManual={onManualSetup}
          onRandomize={onRandomizeSetup}
          canRandomize={isLeader}
        />
      )}
    </div>
    )}
    </Stage>
  );
}
